<!-- =========================================================================================
    File Name: InsightsView.vue
    Description: Org Admins can upload and manage company documents & insights
========================================================================================== -->

<template>
  <div id="page-insights-view">
    <!-- Header Area -->
    <div class="flex items-center justify-between mb-8">
      <div>
        <h2 class="font-bold text-2xl mb-2">Company Insights & Documents</h2>
        <p class="text-gray-600">Upload and share important files, frameworks, and videos with your organization.</p>
      </div>
      
      <!-- Top Upload Button -->
      <div v-if="documents.length > 0">
        <vs-button icon-pack="feather" icon="icon-plus" class="shadow-md" @click="uploadPopupActive = true">
          Add New Material
        </vs-button>
      </div>
    </div>

    <!-- EMPTY STATE: Big Upload Area -->
    <div v-if="documents.length === 0" class="flex items-center justify-center mt-10">
      <vx-card class="w-full lg:w-2/3 text-center p-10 cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-dashed border-primary" @click="uploadPopupActive = true">
        <div class="flex flex-col items-center justify-center py-10">
          <div class="p-6 bg-primary-light rounded-full mb-4 text-primary">
            <feather-icon icon="UploadCloudIcon" svgClasses="w-12 h-12" />
          </div>
          <h3 class="font-bold text-2xl mb-2 text-gray-800">Add your first material</h3>
          <p class="text-gray-500 mb-6">Upload PDFs, Docs, Excels, or link YouTube/Scribd URLs</p>
          <vs-button type="border" icon-pack="feather" icon="icon-plus" class="font-semibold">Click to Add</vs-button>
        </div>
      </vx-card>
    </div>

    <!-- POPULATED STATE: Beautiful Document Grid -->
    <div v-else class="vx-row">
      <div class="vx-col w-full sm:w-1/2 md:w-1/3 lg:w-1/4 mb-6" v-for="(doc, index) in documents" :key="index">
        <vx-card class="h-full flex flex-col hover:-translate-y-1 hover:shadow-lg transition-all duration-300 border border-gray-100">
          
          <div slot="no-body" class="flex flex-col h-full p-5">
            <!-- Card Header: Icon & Title -->
            <div class="flex items-start mb-4">
              <div class="p-3 rounded-lg mr-3" :class="getIconBackground(doc.file_type)">
                <feather-icon :icon="getFileIcon(doc.file_type)" :svgClasses="['w-6 h-6', getFileColor(doc.file_type)]" />
              </div>
              <div class="flex-1 overflow-hidden">
                <h4 class="font-bold text-base text-gray-800 truncate" :title="doc.title">{{ doc.title }}</h4>
                <p class="text-xs text-gray-500 mt-1 font-medium">{{ doc.file_size }} • {{ formatDate(doc.created_at) }}</p>
              </div>
            </div>

            <!-- Card Body: Description -->
            <div class="flex-grow mb-6">
              <p class="text-sm text-gray-600 line-clamp-2" :title="doc.description">
                {{ doc.description || 'No description provided.' }}
              </p>
            </div>

            <!-- Card Footer: Visibility Badge & Actions -->
            <div class="flex items-center justify-between border-t border-gray-100 pt-4 mt-auto">
              <div class="flex flex-col space-y-1">
                <span class="text-[10px] font-bold uppercase tracking-wider text-gray-400">{{ doc.category }}</span>
                <span class="text-xs font-bold px-3 py-1 rounded-full text-center" :class="getVisibilityClass(doc.visibility)">
                  {{ doc.visibility }}
                </span>
              </div>
              
              <div class="flex space-x-3 items-center">
                <a @click.prevent="openDocument(doc.file_url)" class="text-gray-400 hover:text-primary cursor-pointer transition-colors" title="Download/View">
                  <feather-icon :icon="doc.file_type === 'link' ? 'ExternalLinkIcon' : 'DownloadIcon'" svgClasses="w-5 h-5" />
                </a>
                <a @click.prevent="openEditModal(doc)" class="text-gray-400 hover:text-warning cursor-pointer transition-colors" title="Edit Details">
                  <feather-icon icon="EditIcon" svgClasses="w-5 h-5" />
                </a>
                <a @click.prevent="deleteDocument(index)" class="text-gray-400 hover:text-danger cursor-pointer transition-colors" title="Delete">
                  <feather-icon icon="Trash2Icon" svgClasses="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

        </vx-card>
      </div>
    </div>

    <!-- UPLOAD MODAL -->
    <vs-popup class="upload-popup" title="Add Training Material" :active.sync="uploadPopupActive">
      <div class="p-2">
        
        <!-- Toggle: File vs Link -->
        <div class="flex items-center space-x-6 mb-6 pb-4 border-b border-gray-100">
          <vs-radio v-model="newDoc.uploadType" vs-value="file" color="primary">Upload File</vs-radio>
          <vs-radio v-model="newDoc.uploadType" vs-value="link" color="primary">External Link</vs-radio>
        </div>

        <!-- File Upload Area -->
        <div class="vx-row mb-6" v-if="newDoc.uploadType === 'file'">
          <div class="vx-col w-full">
            <input type="file" ref="fileInput" class="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.mp4" @change="onFileSelected" />
            <div 
              class="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
              @click="$refs.fileInput.click()"
            >
              <feather-icon icon="FilePlusIcon" svgClasses="w-10 h-10 text-gray-400 mb-3" />
              <span v-if="!newDoc.file" class="font-medium text-gray-600">Click to select a file</span>
              <span v-else class="font-bold text-primary">{{ newDoc.file.name }}</span>
            </div>
          </div>
        </div>

        <!-- Link Input Area -->
        <div class="vx-row mb-6" v-if="newDoc.uploadType === 'link'">
          <div class="vx-col w-full">
            <vs-input label="External URL (YouTube, Scribd, Drive)" v-model="newDoc.external_url" class="w-full" placeholder="https://..." />
          </div>
        </div>

        <div class="vx-row mb-5">
          <div class="vx-col w-full">
            <vs-input label="Title" v-model="newDoc.title" class="w-full" placeholder="e.g. Q3 Startup Framework" />
          </div>
        </div>

        <div class="vx-row mb-5">
          <div class="vx-col w-full">
            <vs-textarea label="Brief Description" v-model="newDoc.description" placeholder="What is this material about?" />
          </div>
        </div>

        <div class="vx-row mb-6">
          <div class="vx-col w-full sm:w-1/2 mb-4 sm:mb-0">
            <label class="text-sm opacity-75 mb-2 block">Category</label>
            <!-- Native Select to avoid Vuesax Bug -->
            <select v-model="newDoc.category" class="w-full p-3 border border-gray-300 rounded text-gray-700 focus:outline-none focus:border-primary cursor-pointer bg-white">
              <option v-for="(cat, index) in categoryOptions" :key="index" :value="cat">
                {{ cat }}
              </option>
            </select>
          </div>
          <div class="vx-col w-full sm:w-1/2">
            <label class="text-sm opacity-75 mb-2 block">Who can see this?</label>
            <!-- Native Select to avoid Vuesax Bug -->
            <select v-model="newDoc.visibility" class="w-full p-3 border border-gray-300 rounded text-gray-700 focus:outline-none focus:border-primary cursor-pointer bg-white">
              <option v-for="(item, index) in visibilityOptions" :key="index" :value="item.value">
                {{ item.text }}
              </option>
            </select>
          </div>
        </div>

        <div class="flex justify-end pt-4 border-t border-gray-100">
          <vs-button type="border" class="mr-4 text-gray-600 border-gray-300" color="dark" @click="uploadPopupActive = false">Cancel</vs-button>
          <vs-button @click="submitDocument" :disabled="isSubmitDisabled || isUploading">
            {{ isUploading ? 'Saving...' : 'Add Material' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- EDIT MODAL -->
    <vs-popup class="edit-popup" title="Edit Details" :active.sync="editPopupActive">
      <div class="p-2" v-if="editingDoc">
        <div class="vx-row mb-5">
          <div class="vx-col w-full">
            <vs-input label="Title" v-model="editingDoc.title" class="w-full" />
          </div>
        </div>

        <div class="vx-row mb-5">
          <div class="vx-col w-full">
            <vs-textarea label="Brief Description" v-model="editingDoc.description" />
          </div>
        </div>

        <div class="vx-row mb-6">
          <div class="vx-col w-full sm:w-1/2 mb-4 sm:mb-0">
            <label class="text-sm opacity-75 mb-2 block">Category</label>
            <select v-model="editingDoc.category" class="w-full p-3 border border-gray-300 rounded text-gray-700 focus:outline-none focus:border-primary cursor-pointer bg-white">
              <option v-for="(cat, index) in categoryOptions" :key="index" :value="cat">
                {{ cat }}
              </option>
            </select>
          </div>
          <div class="vx-col w-full sm:w-1/2">
            <label class="text-sm opacity-75 mb-2 block">Visibility</label>
            <select v-model="editingDoc.visibility" class="w-full p-3 border border-gray-300 rounded text-gray-700 focus:outline-none focus:border-primary cursor-pointer bg-white">
              <option v-for="(item, index) in visibilityOptions" :key="index" :value="item.value">
                {{ item.text }}
              </option>
            </select>
          </div>
        </div>

        <div class="flex justify-end pt-4 border-t border-gray-100">
          <vs-button type="border" class="mr-4 text-gray-600 border-gray-300" color="dark" @click="editPopupActive = false">Cancel</vs-button>
          <vs-button @click="saveEdit" :disabled="!editingDoc.title || isSaving">
            {{ isSaving ? 'Saving...' : 'Save Changes' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "InsightsView",
  data() {
    return {
      uploadPopupActive: false,
      editPopupActive: false,
      isUploading: false,
      isSaving: false,
      
      newDoc: {
        uploadType: "file", 
        file: null,
        external_url: "",
        title: "",
        description: "",
        category: "Document", // Defaulted to Document
        visibility: "All Users",
      },
      
      editingDoc: null,

      // ADDED "Document" Category!
      categoryOptions: ["Book", "Presentation", "Video", "Document"],
      visibilityOptions: [
        { text: "All Users in Organization", value: "All Users" },
        { text: "Trainees / Founders Only", value: "Founders" },
        { text: "Coaches / Mentors Only", value: "Coaches" },
        { text: "Org Admins Only", value: "Admins" }
      ],
      documents: [] 
    };
  },
  computed: {
    // 1. THE ULTIMATE BULLETPROOF ORG ID FINDER
    currentOrgId() {
      // A. Check Cookies (Primary method for Coach Dashboard)
      if (window.$cookies) {
        const cookieOrg = window.$cookies.get('mongo_org_id') || window.$cookies.get('companyId') || window.$cookies.get('organizationId');
        if (cookieOrg) return cookieOrg;
      }

      // B. Check flat local storage
      const localOrg = localStorage.getItem('mongo_org_id') || localStorage.getItem('companyId') || localStorage.getItem('organizationId') || localStorage.getItem('orgId');
      if (localOrg && localOrg !== 'undefined' && localOrg !== 'null') return localOrg;

      // C. Parse 'userInfo' object from local storage
      try {
        const userInfoStr = localStorage.getItem('userInfo');
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          if (userInfo.mongo_org_id) return userInfo.mongo_org_id;
          if (userInfo.companyId) return userInfo.companyId;
          if (userInfo.organizationId) return userInfo.organizationId;
          if (userInfo.company && userInfo.company.mongo_org_id) return userInfo.company.mongo_org_id;
          if (userInfo.company && userInfo.company.id) return userInfo.company.id;
        }
      } catch (e) {
        // ignore
      }

      // D. Check Vuex Store safely
      const state = this.$store && this.$store.state ? this.$store.state : null;
      if (state) {
        // Look in userManagement (from your console logs)
        if (state.userManagement && state.userManagement.company) {
          if (state.userManagement.company.mongo_org_id) return state.userManagement.company.mongo_org_id;
          if (state.userManagement.company.id) return state.userManagement.company.id;
        }
        // Look in AppActiveUser
        if (state.AppActiveUser) {
          if (state.AppActiveUser.mongo_org_id) return state.AppActiveUser.mongo_org_id;
          if (state.AppActiveUser.companyId) return state.AppActiveUser.companyId;
          if (state.AppActiveUser.organizationId) return state.AppActiveUser.organizationId;
        }
      }

      // E. Check URL parameters
      if (this.$route && this.$route.query) {
        if (this.$route.query.org_id) return this.$route.query.org_id;
        if (this.$route.query.company_id) return this.$route.query.company_id;
      }
      
      // Fallback
      return ""; 
    },
    
    currentUserId() {
      // Check Cookies first
      if (window.$cookies && window.$cookies.get('userId')) return window.$cookies.get('userId');

      // Check Vuex
      const state = this.$store && this.$store.state ? this.$store.state : null;
      if (state && state.AppActiveUser && state.AppActiveUser.uid) {
        return state.AppActiveUser.uid;
      }

      // Check LocalStorage
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        if (userInfo && (userInfo.uid || userInfo.id || userInfo._id)) {
          return userInfo.uid || userInfo.id || userInfo._id;
        }
      } catch(e) {}

      return localStorage.getItem('userId') || "admin";
    },

    isSubmitDisabled() {
      if (!this.newDoc.title) return true;
      if (this.newDoc.uploadType === 'file' && !this.newDoc.file) return true;
      if (this.newDoc.uploadType === 'link' && !this.newDoc.external_url) return true;
      return false;
    }
  },
  methods: {
    // 1. Fetch data
    async fetchDocuments() {
      if (!this.currentOrgId) return;
      try {
        const response = await axios.get(`https://parser.ai.getboarded.com/api/v2/organization/${this.currentOrgId}/insights`);
        if (response.data.status === 'success') {
          this.documents = response.data.data;
        }
      } catch (error) {
        console.error("Failed to fetch documents", error);
      }
    },

    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.newDoc.file = file;
        if (!this.newDoc.title) {
          this.newDoc.title = file.name.split('.')[0]; 
        }
      }
    },

    // 2. Upload Document or Link
    async submitDocument() {
      if (!this.currentOrgId) {
        this.$vs.notify({ color: "danger", title: "Error", text: "Organization ID missing. Please refresh or relogin." });
        return;
      }
      
      this.isUploading = true;
      const formData = new FormData();
      
      if (this.newDoc.uploadType === 'file') {
        formData.append("file", this.newDoc.file);
      } else {
        formData.append("external_url", this.newDoc.external_url);
      }
      
      formData.append("title", this.newDoc.title);
      formData.append("description", this.newDoc.description);
      formData.append("visibility", this.newDoc.visibility);
      formData.append("category", this.newDoc.category);
      formData.append("uploaded_by", this.currentUserId);

      try {
        const response = await axios.post(`https://parser.ai.getboarded.com/api/v2/organization/${this.currentOrgId}/insights`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });

        if (response.data.status === 'success') {
          this.$vs.notify({ color: "success", title: "Success", text: "Material added successfully!" });
          await this.fetchDocuments(); 
          this.uploadPopupActive = false;
          
          // Reset Form
          this.newDoc = { 
            uploadType: "file", file: null, external_url: "", 
            title: "", description: "", category: "Document", visibility: "All Users" 
          };
          if(this.$refs.fileInput) this.$refs.fileInput.value = '';
        }
      } catch (error) {
        this.$vs.notify({ color: "danger", title: "Error", text: "Failed to upload material." });
      } finally {
        this.isUploading = false;
      }
    },

    // 3. EDIT Modals & Actions
    openEditModal(doc) {
      this.editingDoc = { 
        id: doc.id,
        title: doc.title, 
        description: doc.description || '', 
        category: doc.category || 'Document',
        visibility: doc.visibility || 'All Users'
      };
      this.editPopupActive = true;
    },

    async saveEdit() {
      if (!this.currentOrgId || !this.editingDoc) return;
      
      this.isSaving = true;
      try {
        const payload = {
          title: this.editingDoc.title,
          description: this.editingDoc.description,
          category: this.editingDoc.category,
          visibility: this.editingDoc.visibility
        };

        const response = await axios.patch(`https://parser.ai.getboarded.com/api/v2/organization/${this.currentOrgId}/insights/${this.editingDoc.id}`, payload);
        
        if (response.data.status === 'success') {
          this.$vs.notify({ color: "success", title: "Updated", text: "Details updated successfully." });
          await this.fetchDocuments(); 
          this.editPopupActive = false;
        }
      } catch (error) {
        this.$vs.notify({ color: "danger", title: "Error", text: "Failed to update." });
      } finally {
        this.isSaving = false;
      }
    },

    // 4. Delete Document
    deleteDocument(index) {
      const doc = this.documents[index];
      
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "Confirm Delete",
        text: `Are you sure you want to delete "${doc.title}"?`,
        accept: async () => {
          try {
            await axios.delete(`https://parser.ai.getboarded.com/api/v2/organization/${this.currentOrgId}/insights/${doc.id}`);
            this.$vs.notify({ color: "success", title: "Deleted", text: "Material removed." });
            await this.fetchDocuments(); 
          } catch (error) {
            this.$vs.notify({ color: "danger", title: "Error", text: "Could not delete." });
          }
        }
      });
    },

    openDocument(url) {
      if(url) {
        // Check if it's a backend file path
        if (url.startsWith('/uploads')) {
           window.open(`https://parser.ai.getboarded.com${url}`, '_blank');
        } else {
           window.open(url, '_blank');
        }
      }
    },

    // UI Formatting Helpers
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    getFileIcon(type) {
      if (type === 'pdf') return 'FileTextIcon';
      if (type === 'word') return 'FileIcon';
      if (type === 'excel') return 'PieChartIcon';
      if (type === 'video') return 'VideoIcon';
      if (type === 'link') return 'LinkIcon';
      return 'FileIcon'; // default for Document
    },
    getFileColor(type) {
      if (type === 'pdf') return 'text-danger';
      if (type === 'word') return 'text-primary';
      if (type === 'excel') return 'text-success';
      if (type === 'video') return 'text-warning';
      if (type === 'link') return 'text-dark';
      return 'text-primary'; // default for Document
    },
    getIconBackground(type) {
      if (type === 'pdf') return 'bg-danger-light';
      if (type === 'word') return 'bg-primary-light';
      if (type === 'excel') return 'bg-success-light';
      if (type === 'video') return 'bg-warning-light';
      if (type === 'link') return 'bg-gray-200';
      return 'bg-primary-light'; // default for Document
    },
    getVisibilityClass(visibility) {
      if (visibility === 'All Users') return 'bg-success-light text-success';
      if (visibility === 'Founders') return 'bg-primary-light text-primary';
      if (visibility === 'Coaches') return 'bg-warning-light text-warning';
      if (visibility === 'Admins') return 'bg-danger-light text-danger';
      return 'bg-gray-200 text-gray-700';
    }
  },
  created() {
    this.fetchDocuments(); 
  }
};
</script>

<style lang="scss">
#page-insights-view {
  .bg-primary-light { background-color: rgba(var(--vs-primary), 0.12) !important; }
  .bg-success-light { background-color: rgba(var(--vs-success), 0.12) !important; }
  .bg-warning-light { background-color: rgba(var(--vs-warning), 0.12) !important; }
  .bg-danger-light  { background-color: rgba(var(--vs-danger), 0.12) !important; }
  
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}
</style>